import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ChevronLeft, ChevronRight, Calendar, Car } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS_FR = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const DAYS_FR = ["Lu","Ma","Me","Je","Ve","Sa","Di"];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1; // Monday=0
}

function CalendarMonth({ year, month, reservations }) {
  const days = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = new Date();
  const todayStr = today.toISOString().split("T")[0];

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);

  return (
    <div>
      <h3 className="text-sm font-bold text-center text-gray-800 mb-3 uppercase tracking-wider">
        {MONTHS_FR[month]} {year}
      </h3>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {DAYS_FR.map(d => (
          <div key={d} className="text-center text-[10px] font-semibold text-gray-400 py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} />;
          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const res = reservations[dateStr];
          const isPast = dateStr < todayStr;
          const isToday = dateStr === todayStr;

          let bg = "bg-white hover:bg-gray-50";
          let text = "text-gray-700";
          let ring = "";
          if (isPast) { bg = "bg-gray-50"; text = "text-gray-300"; }
          else if (res?.statut === "réservé") { bg = "bg-red-100"; text = "text-red-700 font-semibold"; }
          else if (res?.statut === "devis" || res?.statut === "en_attente") { bg = "bg-amber-100"; text = "text-amber-700"; }
          if (isToday) ring = "ring-2 ring-[#D4A843]";

          const label = res ? (res.statut === "réservé" ? "Réservé" : "En cours") : null;

          return (
            <div key={dateStr} title={label || "Disponible"}
              className={`relative aspect-square flex items-center justify-center text-xs rounded ${bg} ${text} ${ring} transition-colors`}>
              {day}
              {res && !isPast && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-current" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DisponibilitesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState("all");
  const [reservationsByVehicle, setReservationsByVehicle] = useState({});
  const [loading, setLoading] = useState(true);

  const today = new Date();
  const [startMonth, setStartMonth] = useState({ year: today.getFullYear(), month: today.getMonth() });

  const NUM_MONTHS = 6;
  const months = [];
  for (let i = 0; i < NUM_MONTHS; i++) {
    let m = startMonth.month + i;
    let y = startMonth.year + Math.floor(m / 12);
    m = m % 12;
    months.push({ year: y, month: m });
  }

  useEffect(() => {
    axios.get(`${API}/vehicles`).then(r => {
      setVehicles(r.data.filter(v => v.is_available !== false));
    });
  }, []);

  const fetchReservations = useCallback(async () => {
    if (!vehicles.length) return;
    setLoading(true);
    const results = {};
    await Promise.all(vehicles.map(async v => {
      try {
        const r = await axios.get(`${API}/vehicles/${v.id}/reserved-dates`);
        results[v.id] = {};
        r.data.forEach(d => { results[v.id][d.date] = { statut: d.statut, vehicle: v.name }; });
      } catch {}
    }));
    setReservationsByVehicle(results);
    setLoading(false);
  }, [vehicles]);

  useEffect(() => { fetchReservations(); }, [fetchReservations]);

  // Merge reservations across all vehicles or filter by selected
  const mergedReservations = {};
  if (selectedVehicleId === "all") {
    vehicles.forEach(v => {
      const vRes = reservationsByVehicle[v.id] || {};
      Object.entries(vRes).forEach(([date, info]) => {
        if (!mergedReservations[date] || info.statut === "réservé") {
          mergedReservations[date] = info;
        }
      });
    });
  } else {
    Object.assign(mergedReservations, reservationsByVehicle[selectedVehicleId] || {});
  }

  const selectedVehicle = vehicles.find(v => v.id === selectedVehicleId);
  const takenDates = Object.values(mergedReservations).length;
  const reservedDates = Object.values(mergedReservations).filter(r => r.statut === "réservé").length;

  const prevPeriod = () => {
    setStartMonth(prev => {
      let m = prev.month - NUM_MONTHS;
      let y = prev.year + Math.floor(m / 12);
      if (m < 0) { m = 12 + (m % 12); if (m === 12) m = 0; y = prev.year - 1 - Math.floor((-prev.month + NUM_MONTHS - 1) / 12) + 1; }
      const d = new Date(prev.year, prev.month - NUM_MONTHS, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };
  const nextPeriod = () => {
    const d = new Date(startMonth.year, startMonth.month + NUM_MONTHS, 1);
    setStartMonth({ year: d.getFullYear(), month: d.getMonth() });
  };

  return (
    <div className="font-sans min-h-screen flex flex-col bg-[#f8f5ef]">
      <Navbar />
      <div className="flex-1">
        {/* Hero */}
        <section className="py-12 text-center bg-[#f8f5ef]">
          <p className="text-xs font-semibold tracking-[0.3em] text-[#D4A843] uppercase mb-2">Calendrier</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3">Disponibilités</h1>
          <p className="text-gray-500 max-w-xl mx-auto">Consultez les dates disponibles pour la Porsche Macan et planifiez votre mariage en toute sérénité.</p>
        </section>

        <div className="max-w-6xl mx-auto px-4 pb-16">
          {/* Vehicle selector */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            <button
              onClick={() => setSelectedVehicleId("all")}
              data-testid="vehicle-filter-all"
              className={`px-5 py-2.5 text-sm font-semibold transition-all ${selectedVehicleId === "all" ? "bg-[#1B2E4B] text-white" : "bg-white text-gray-600 border border-gray-200 hover:border-gray-400"}`}>
              Porsche Macan
            </button>
            {vehicles.map(v => (
              <button key={v.id}
                onClick={() => setSelectedVehicleId(v.id)}
                data-testid={`vehicle-filter-${v.id}`}
                className={`px-5 py-2.5 text-sm font-semibold transition-all flex items-center gap-2 ${selectedVehicleId === v.id ? "bg-[#D4A843] text-white" : "bg-white text-gray-600 border border-gray-200 hover:border-gray-400"}`}>
                <Car size={14} /> {v.name}
              </button>
            ))}
          </div>

          {/* Stats banner */}
          {!loading && (
            <div className="flex flex-wrap justify-center gap-6 mb-8 text-center">
              <div className="bg-white px-6 py-3 shadow-sm rounded">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">Dates prises</p>
                <p className="text-2xl font-bold text-gray-800">{takenDates}</p>
              </div>
              <div className="bg-white px-6 py-3 shadow-sm rounded">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">Réservées</p>
                <p className="text-2xl font-bold text-red-600">{reservedDates}</p>
              </div>
              <div className="bg-white px-6 py-3 shadow-sm rounded">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">En attente</p>
                <p className="text-2xl font-bold text-amber-600">{takenDates - reservedDates}</p>
              </div>
            </div>
          )}

          {/* Period navigation */}
          <div className="flex items-center justify-between mb-6">
            <button onClick={prevPeriod} className="p-2 bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors">
              <ChevronLeft size={18} />
            </button>
            <span className="text-sm font-semibold text-gray-600">
              {MONTHS_FR[months[0].month]} {months[0].year} — {MONTHS_FR[months[NUM_MONTHS - 1].month]} {months[NUM_MONTHS - 1].year}
            </span>
            <button onClick={nextPeriod} data-testid="next-period-btn" className="p-2 bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors">
              <ChevronRight size={18} />
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#D4A843] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {months.map(({ year, month }) => (
                <div key={`${year}-${month}`} className="bg-white p-5 shadow-sm rounded">
                  <CalendarMonth year={year} month={month} reservations={mergedReservations} />
                </div>
              ))}
            </div>
          )}

          {/* Legend */}
          <div className="flex flex-wrap items-center justify-center gap-5 mt-8 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-white border border-gray-200 rounded" />
              <span>Disponible</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-amber-100 rounded" />
              <span>Devis en cours</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-red-100 rounded" />
              <span>Réservé</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-gray-50 border border-gray-100 rounded" />
              <span>Date passée</span>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-10">
            <p className="text-gray-500 mb-4">Votre date est disponible ? Faites votre demande maintenant !</p>
            <a href="/reservation" className="inline-block px-10 py-4 bg-[#D4A843] text-white font-bold tracking-wider hover:bg-[#c49933] transition-colors">
              RÉSERVER MA DATE
            </a>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
